<template>
  <div v-if="householdMember">
    <v-row class="fs-16 d-flex flex-row align-end">
      <LabeledTextfield
        v-model="householdMember.first_name"
        :autofocus="autofocus"
        :hard-lock="hardLock"
        :schema-id="schema.id"
        cols="12"
        field="first_name"
        sm="4"
        mandatory
      />
      <LabeledTextfield
        v-model="householdMember.middle_name"
        :hard-lock="hardLock"
        :schema-id="schema.id"
        cols="12"
        field="middle_name"
        placeholder="Optional"
        sm="4"
      />
      <LabeledTextfield
        v-model="householdMember.last_name"
        :hard-lock="hardLock"
        :schema-id="schema.id"
        cols="12"
        field="last_name"
        sm="4"
        mandatory
      />
    </v-row>
    <v-row class="fs-16 d-flex flex-row align-end">
      <LabeledSimpleSelect
        v-model="householdMember.relationship"
        :hard-lock="hardLock"
        :item-props="true"
        :items="householdMemberFields(relationshipItems)"
        :schema-id="schema.id"
        cols="12"
        field="relationship"
        item-title="text"
        item-value="value"
        md="6"
        mandatory
      />
      <LabeledTextfield
        v-if="!hideSsn"
        v-model="householdMember.ssn"
        :hard-lock="hardLock"
        :schema-id="schema.id"
        cols="12"
        field="ssn"
        md="6"
        password-toggle-hide-label="Hide SSN"
        password-toggle-show-label="Show SSN"
        type="password"
      />
    </v-row>

    <v-row class="fs-16 d-flex flex-row align-end">
      <LabeledSimpleSelect
        v-model="householdMember.gender"
        :hard-lock="hardLock"
        :item-props="true"
        :items="householdMemberFields(schema.definition.properties.gender.enum)"
        :schema-id="schema.id"
        cols="12"
        field="gender"
        item-title="text"
        item-value="value"
        md="4"
        mandatory
      />
      <LabeledSimpleSelect
        v-model="householdMember.race"
        :hard-lock="hardLock"
        :item-props="true"
        :items="householdMemberFields(schema.definition.properties.race.items.enum)"
        :schema-id="schema.id"
        cols="12"
        field="race"
        item-title="text"
        item-value="value"
        md="4"
        multiple
      />
      <LabeledSimpleSelect
        v-model="householdMember.ethnicity"
        :hard-lock="hardLock"
        :item-props="true"
        :items="householdMemberFields(schema.definition.properties.ethnicity.enum)"
        :schema-id="schema.id"
        cols="12"
        field="ethnicity"
        item-title="text"
        item-value="value"
        md="4"
        mandatory
      />
    </v-row>
    <v-row class="fs-16 d-flex flex-row align-end">
      <LabeledDateOfBirth
        v-model="householdMember.dob"
        :hard-lock="hardLock"
        :schema-id="schema.id"
        cols="12"
        field="dob"
        message="Date of birth"
        mandatory
      />
    </v-row>
    <v-row v-if="!hardLock">
      <v-col
        class="d-flex justify-start"
        cols="6"
      >
        <v-btn
          @click="cancel"
          :loading="processing"
          :ripple="false"
          class="focus-very-visible me-3"
          color="primary"
          size="large"
          variant="outlined"
        >
          {{ $bct('Cancel') }}
        </v-btn>
      </v-col>
      <v-col
        class="d-flex justify-end"
        cols="6"
      >
        <v-btn
          v-if="!archiveDisabled"
          @click="archive"
          :loading="processing"
          :ripple="false"
          class="focus-very-visible me-3"
          color="red"
          size="large"
          variant="flat"
        >
          {{ $bct(archiveLabel) }}
        </v-btn>
        <v-btn
          @click="save"
          :loading="processing"
          :ripple="false"
          class="focus-very-visible me-3"
          color="primary"
          size="large"
        >
          {{ $bct('Save') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';

import { householdMemberDefaults } from '@/shared/components/household-members/HouseholdMember';
import LabeledDateOfBirth from '@/shared/components/form/LabeledDateOfBirth.vue';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';

const emit = defineEmits(['save', 'cancel', 'householdMemberChanged', 'archive']);
const props = defineProps({
  autofocus: {
    type: Boolean,
    default: false,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  hardLock: {
    type: Boolean,
    default: false,
  },
  householdMemberValues: {
    type: Object,
    default() {
      return householdMemberDefaults;
    },
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  archiveDisabled: {
    type: Boolean,
    default: false,
  },
  archiveLabel: {
    type: String,
    default: 'Archive',
  },
  selfExists: {
    type: Boolean,
    default: false,
  },
  spouseExists: {
    type: Boolean,
    default: false,
  },
  hideSsn: {
    type: Boolean,
    default: false,
  },
});

const $bct = inject('translator');
const store = useStore();

const householdMember = ref(null);

defineExpose({ resetDefaults });

const schema = computed(() => {
  return Object.values(store.state.schemas).find((val) => val.data_type === 'HouseholdMember');
});

const relationshipItems = computed(() => {
  const exclude = [];

  if (props.selfExists) {
    exclude.push('self');
  }

  if (!props.spouseExists) {
    exclude.push('spouse');
  }

  const enumItems = schema.value.definition.properties.relationship.enum || [];

  return enumItems.filter((item) => {
    return !exclude.includes(item?.toLowerCase());
  });
});

function cancel() {
  emit('cancel');
  resetDefaults();
}

function getCopiedValue() {
  return JSON.parse(JSON.stringify(props.householdMemberValues));
}

function householdMemberFields(fields) {
  return (fields || []).map((fieldName) => {
    return {
      text: $bct(fieldName),
      value: fieldName,
    };
  });
}

function archive() {
  emit('archive', householdMember.value);
}

function resetDefaults() {
  if (!props.editMode) {
    householdMember.value = { ...householdMemberDefaults };
  }
}

function save() {
  emit('save', householdMember.value);
}

watch(
  householdMember,
  (newVal) => {
    emit('householdMemberChanged', newVal);
  },
  { deep: true },
);

onMounted(() => {
  householdMember.value = getCopiedValue();
});
</script>
